import React from 'react';
import {
  Box,
  Button,
  Badge,
  IconButton,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { useTranslation } from 'react-i18next';
import type { SessionTasksProps } from './types';

const SessionTasksSection: React.FC<SessionTasksProps> = ({
  tasks,
  tasksLoading,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onNavigateToAllTasks,
  onManageAttachments,
  renderStatusLabel,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Paper
      sx={{
        p: 2.5,
        bgcolor: '#fff',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            {t('coachingNavigator.sessionDetails.tasksTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={onNavigateToAllTasks}
            >
              {t('coachingNavigator.sessionDetails.tasksSeeAll')}
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none' }}
              onClick={onAddTask}
            >
              {t('coachingNavigator.sessionDetails.tasksAdd')}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
              <Box
                component="tr"
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {t('coachingNavigator.sessionDetails.tasksDescription')}
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {t('coachingNavigator.tasks.session') || 'Session'}
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {t('coachingNavigator.sessionDetails.tasksAssigned')}
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {t('coachingNavigator.sessionDetails.tasksDueDate')}
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {t('coachingNavigator.sessionDetails.tasksStatus')}
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    fontSize: 12,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                ></Box>
              </Box>
            </Box>
            <Box component="tbody">
              {tasksLoading ? (
                <Box
                  component="tr"
                  sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box
                    component="td"
                    colSpan={6}
                    sx={{
                      px: 2,
                      py: 1.5,
                      fontSize: 14,
                      color: 'text.secondary',
                    }}
                  >
                    {t('coachingNavigator.methods.loading')}
                  </Box>
                </Box>
              ) : tasks.length === 0 ? (
                <Box
                  component="tr"
                  sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box
                    component="td"
                    colSpan={6}
                    sx={{ px: 2, py: 1.5, fontSize: 14 }}
                  >
                    {t('coachingNavigator.sessionDetails.tasksEmpty')}
                  </Box>
                </Box>
              ) : (
                tasks.map((task) => (
                  <Box
                    key={task.id || task.content}
                    component="tr"
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      component="td"
                      sx={{ px: 2, py: 1.5, fontSize: 14 }}
                    >
                      {task.content}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        px: 2,
                        py: 1.5,
                        fontSize: 14,
                        color: 'text.secondary',
                      }}
                    >
                      {task.sessionName || '-'}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        px: 2,
                        py: 1.5,
                        fontSize: 14,
                        color: 'text.secondary',
                      }}
                    >
                      {task.actor || '-'}
                    </Box>
                    <Box
                      component="td"
                      sx={{ px: 2, py: 1.5, fontSize: 14 }}
                    >
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString(i18n.language || undefined)
                        : '-'}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        px: 2,
                        py: 1.5,
                        fontSize: 14,
                        color: 'text.secondary',
                      }}
                    >
                      {renderStatusLabel(task.status)}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        px: 2,
                        py: 1.5,
                        fontSize: 14,
                        display: 'flex',
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => onEditTask(task)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <Badge color="primary" badgeContent={task.attachments || 0} overlap="circular">
                        <IconButton size="small" onClick={() => onManageAttachments(task)}>
                          <AttachFileOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Badge>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDeleteTask(task.id)}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SessionTasksSection;
